// Delete company — the destructive card at the foot of the General tab.
//
// Gated by typing the company name, the way ProjectDeleteModal gates a project
// delete. There is no "archive the company first" step, so the typed name IS
// the precondition.
//
// TODO(backend): no delete endpoint exists yet. `confirmDelete` below is the
// sequence to run once the path lands — it is deliberately left unwired rather
// than pointed at a guessed route. The DEV_ONLY nav gate keeps this off prod.

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Card, ConfirmModal, Input } from '@/components/common';
import { selectCompanyDetail } from '@/redux/auth/selectors';
import { useAppSelector } from '@/store/hooks';

const POINT_KEYS = [
  'company_settings_delete_point_data',
  'company_settings_delete_point_access',
  'company_settings_delete_point_export',
];

const DeleteCompanyCard = () => {
  const { t } = useTranslation();
  const companyDetail = useAppSelector(selectCompanyDetail);
  const name = companyDetail?.companyName ?? '';

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [text, setText] = useState('');

  // Reset on every open so an abandoned earlier attempt can't leave the
  // confirm button pre-enabled.
  useEffect(() => setText(''), [confirmOpen]);

  const confirmDelete = () => {
    // TODO(backend): await deleteCompany().unwrap(), then tear the session
    // down — navigate('/login', { replace: true }) BEFORE dispatch(clearAuth()),
    // or JWTProtectedRoute redirects first and stashes state.from='/settings',
    // a route that no longer exists for this user. clearAuth's store listener
    // resets the RTK Query cache, so no manual resetApiState is needed.
    //
    // Until then this says so out loud and leaves the dialog open. Closing it
    // silently would read as a successful delete, which is the one outcome an
    // owner must never be misled about.
    toast.error(t('company_settings_delete_unavailable'));
  };

  return (
    <>
      <Card
        className='company-settings__card--danger'
        padded={false}
        head={
          <div className='company-settings__card-head'>
            <span className='company-settings__card-heading'>
              <span className='company-settings__card-title'>
                {t('company_settings_delete_title')}
              </span>
              <span className='company-settings__card-subtitle'>
                {t('company_settings_delete_subtitle')}
              </span>
            </span>
          </div>
        }
      >
        <div className='company-settings__danger-body'>
          <ul className='company-settings__danger-list'>
            {POINT_KEYS.map((key) => (
              <li key={key} className='company-settings__danger-item'>
                {t(key)}
              </li>
            ))}
          </ul>
          <span className='company-settings__danger-action'>
            <Button
              variant='danger-outline'
              iconStart='trash-2'
              onClick={() => setConfirmOpen(true)}
            >
              {t('company_settings_delete_title')}
            </Button>
          </span>
        </div>
      </Card>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        tone='danger'
        icon='trash-2'
        title={t('company_settings_delete_title')}
        description={
          <Trans
            i18nKey='company_settings_delete_confirm'
            values={{ name }}
            components={{ b: <strong /> }}
          />
        }
        confirmLabel={t('company_settings_delete_title')}
        confirmVariant='danger'
        confirmIcon='trash-2'
        confirmDisabled={text.trim() !== name}
      >
        <div className='company-settings__delete-confirm'>
          {/* Placeholder is the company name verbatim — data, not copy. */}
          <Input
            value={text}
            placeholder={name}
            aria-label={t('company_settings_delete_type_name')}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </ConfirmModal>
    </>
  );
};

export default DeleteCompanyCard;
